export type WarehousePositionStatus = 'livre' | 'ocupado' | 'bloqueado';

export interface WarehousePosition {
  id: string;
  rua: string;
  coluna: number;
  nivel: number;
  status: WarehousePositionStatus;
  tipo: string;
  capacidade: number;
  codigoCompleto?: string;
  quantidadeAtual?: number;
  // Linked recebimento info
  itemDescricao?: string | null;
  itemQuantidade?: number | null;
  recNumeroNf?: string | null;
  recFornecedor?: string | null;
  recDataChegada?: string | null;
}

export interface WarehouseStreetColumn {
  coluna: number;
  posicoes: WarehousePosition[];
}

export interface WarehouseStreetGroup {
  rua: string;
  colunas: WarehouseStreetColumn[];
}

const parseStreet = (street: string): { prefix: string; number: number } => {
  const parts = street.split('-');
  const prefix = parts[0] || street;
  const number = Number(parts[1] || 0);
  return { prefix, number };
};

const sortByStreet = (a: string, b: string) => {
  const sa = parseStreet(a);
  const sb = parseStreet(b);
  if (sa.prefix !== sb.prefix) {
    return sa.prefix.localeCompare(sb.prefix, 'pt-BR');
  }
  return sa.number - sb.number;
};

export const groupPositionsByStreet = (positions: WarehousePosition[]): WarehouseStreetGroup[] => {
  const byStreet = new Map<string, WarehousePosition[]>();

  for (const position of positions) {
    const list = byStreet.get(position.rua) || [];
    list.push(position);
    byStreet.set(position.rua, list);
  }

  const groups: WarehouseStreetGroup[] = [];

  for (const [street, streetPositions] of byStreet.entries()) {
    const byColumn = new Map<number, WarehousePosition[]>();

    for (const position of streetPositions) {
      const colPositions = byColumn.get(position.coluna) || [];
      colPositions.push(position);
      byColumn.set(position.coluna, colPositions);
    }

    const columns: WarehouseStreetColumn[] = Array.from(byColumn.entries())
      .map(([column, columnPositions]) => ({
        coluna: column,
        posicoes: columnPositions.sort((a, b) => a.nivel - b.nivel)
      }))
      .sort((a, b) => a.coluna - b.coluna);

    groups.push({
      rua: street,
      colunas: columns
    });
  }

  return groups.sort((a, b) => sortByStreet(a.rua, b.rua));
};

export const getStatusColorClass = (status: WarehousePositionStatus): string => {
  if (status === 'livre') return 'wms-wh-pos-livre';
  if (status === 'ocupado') return 'wms-wh-pos-ocupado';
  return 'wms-wh-pos-bloqueado';
};

export const formatShortPositionCode = (column: number): string => {
  return String(column).padStart(2, '0');
};
